import { Loadable } from '@/components/loaders';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { BaseAudience } from '@/models/audiences';
import { ConnectorFlowDTO, ConnectorInstanceSafeDTO } from '@/models/connectors';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import Form from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS } from '@/features/destinations/components/connectorFormConfig';
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type StepConfig = {
  handler?: string;
  reactJsonSchemaForm?: {
    formId?: string;
    title?: string;
    description?: string;
    schema: RJSFSchema;
    uiSchema: Record<string, any>;
  };
};

type MappingsConfig = {
  schema: RJSFSchema;
  uiSchema: Record<string, any>;
} | null;

type MultiStepFlowFormProps = {
  audience: BaseAudience;
  connectorInstance: ConnectorInstanceSafeDTO;
  flow: ConnectorFlowDTO;
  paramsConfig: Record<string, StepConfig>;
  onComplete: (allParams: Record<string, any>, mappingsConfig: MappingsConfig) => void;
};

const isSchemaAllHidden = (form: { schema?: RJSFSchema; uiSchema?: Record<string, any> } | undefined): boolean => {
  const props = (form?.schema as any)?.properties || {};
  const keys = Object.keys(props);
  if (keys.length === 0) return true;
  const ui = form?.uiSchema || {};
  return keys.every((k) => ui[k]?.['ui:widget'] === 'hidden');
};

const collectDefaults = (schema: RJSFSchema | undefined): Record<string, any> => {
  const data: Record<string, any> = {};
  const props = (schema as any)?.properties || {};
  for (const [key, prop] of Object.entries<any>(props)) {
    if (prop?.default !== undefined) data[key] = prop.default;
  }
  return data;
};

export const MultiStepFlowForm: FC<MultiStepFlowFormProps> = ({ audience, connectorInstance, flow, paramsConfig, onComplete }) => {
  const { t } = useTranslation();
  const { id: workspaceId } = useWorkspace();
  const [prepareStep, { isLoading: isLoadingStep }] = usePrepareConnectorFlowStepMutation();
  const dispatch = useAppDispatch();

  const { currentLanguage } = useCompanyValues();

  // Get sorted step keys (step1, step2, step3, ...)
  const stepKeys = useMemo(() => {
    return Object.keys(paramsConfig)
      .filter((key) => /^step\d+$/.test(key))
      .sort((a, b) => parseInt(a.replace('step', '')) - parseInt(b.replace('step', '')));
  }, [paramsConfig]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentStepSchema, setCurrentStepSchema] = useState<{
    schema: RJSFSchema;
    uiSchema: Record<string, any>;
    title?: string;
  } | null>(null);
  const [currentFormData, setCurrentFormData] = useState<Record<string, any>>({});
  const [accumulatedData, setAccumulatedData] = useState<Record<string, any>>({});
  const [isInitializing, setIsInitializing] = useState(true);
  const [lastStepMappings, setLastStepMappings] = useState<MappingsConfig>(null);
  const [autoSkippedSteps, setAutoSkippedSteps] = useState<Set<string>>(new Set());

  // Use ref to track if we've initialized
  const hasInitialized = useRef(false);

  const isLastStep = currentStepIndex === stepKeys.length - 1;
  const totalSteps = stepKeys.length;

  // Load step schema from API and capture mappings if present in response
  const loadStepSchema = async (stepIndex: number, dataToSend: Record<string, any>): Promise<void> => {
    // For step N, we call POST /prepare/step{N} with data from previous steps
    // - For step1 (index 0): POST /prepare/step1 with empty data
    // - For step2 (index 1): POST /prepare/step2 with step1 data
    // - For step3 (index 2): POST /prepare/step3 with step1+step2 data
    const stepIdToCall = stepKeys[stepIndex]; // step1, step2, step3, etc.
    const bodyToSend = stepIndex === 0 ? {} : dataToSend;

    // Check for autofill static config formId before hitting the API
    const staticFormId = paramsConfig[stepIdToCall]?.reactJsonSchemaForm?.formId;
    if (AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS.includes(staticFormId)) {
      const autoFillData = { audience_name: audience.name, createdOn: audience.createdOn };
      const newAccumulatedData = { ...dataToSend, ...autoFillData };
      setAccumulatedData(newAccumulatedData);

      if (stepIndex === stepKeys.length - 1) {
        onComplete(newAccumulatedData, null);
      } else {
        const nextStepIndex = stepIndex + 1;
        setCurrentStepIndex(nextStepIndex);
        setCurrentFormData({});
        await loadStepSchema(nextStepIndex, newAccumulatedData);
      }
      return;
    }

    try {
      const response = await prepareStep({
        workspaceId,
        flowId: flow.id,
        stepId: stepIdToCall,
        audienceId: audience.id,
        connectorInstanceId: connectorInstance.id,
        stepData: bodyToSend,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      const translatedParamsConfig = translateI18nValues(paramsConfig, currentLanguage);

      // Extract the reactJsonSchemaForm for the current step from the prepared response.
      // The API returns the full flow object with the schema nested at:
      // response.configuration.elements.params.<stepKey>.reactJsonSchemaForm
      const preparedStepConfig = translatedResponse?.configuration?.elements?.params?.[stepKeys[stepIndex]];
      const formSchema =
        preparedStepConfig?.reactJsonSchemaForm ||
        translatedResponse?.reactJsonSchemaForm ||
        translatedResponse?.configuration?.reactJsonSchemaForm ||
        translatedParamsConfig[stepKeys[stepIndex]]?.reactJsonSchemaForm;

      // Capture mappings if present in response (will be used when all steps are done)
      // Path: response.configuration.elements.mappings.reactJsonSchemaForm
      const mappingsSchema =
        translatedResponse?.configuration?.elements?.mappings?.reactJsonSchemaForm ||
        translatedResponse?.configuration?.mappings?.reactJsonSchemaForm ||
        translatedResponse?.mappings?.reactJsonSchemaForm;
      const currentMappings: MappingsConfig = mappingsSchema?.schema ? { schema: mappingsSchema.schema, uiSchema: mappingsSchema.uiSchema || {} } : null;
      if (currentMappings) {
        setLastStepMappings(currentMappings);
      }

      if (formSchema) {
        if (AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS.includes(formSchema.formId)) {
          const autoFillData = { audience_name: audience.name, createdOn: audience.createdOn };
          const newAccumulatedData = { ...dataToSend, ...autoFillData };
          setAccumulatedData(newAccumulatedData);

          if (stepIndex === stepKeys.length - 1) {
            onComplete(newAccumulatedData, currentMappings);
          } else {
            const nextStepIndex = stepIndex + 1;
            setCurrentStepIndex(nextStepIndex);
            setCurrentFormData({});
            await loadStepSchema(nextStepIndex, newAccumulatedData);
          }
          return;
        }

        // Auto-skip steps that have only hidden fields (or no fields at all).
        // Hidden defaults are already populated by the backend (addHiddenFieldToSchema),
        // so we just collect them and advance.
        if (isSchemaAllHidden(formSchema)) {
          const autoData = collectDefaults(formSchema.schema);
          const newAccumulatedData = { ...dataToSend, ...autoData };
          setAccumulatedData(newAccumulatedData);
          setAutoSkippedSteps((prev) => new Set(prev).add(stepIdToCall));

          if (stepIndex === stepKeys.length - 1) {
            onComplete(newAccumulatedData, currentMappings);
          } else {
            const nextStepIndex = stepIndex + 1;
            setCurrentStepIndex(nextStepIndex);
            setCurrentFormData({});
            await loadStepSchema(nextStepIndex, newAccumulatedData);
          }
          return;
        }

        setCurrentStepSchema({
          schema: formSchema.schema,
          uiSchema: formSchema.uiSchema || {},
          title: formSchema.title,
        });
      }
    } catch (error: any) {
      // Fallback to static config
      console.error('---------Failed to load step schema, using fallback:---------', error);
      const errorMessage = error?.data?.message || 'Something went wrong, Could not Proceed further';
      dispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));

      // const fallbackSchema = paramsConfig[stepKeys[stepIndex]]?.reactJsonSchemaForm;
      // if (fallbackSchema) {
      //   setCurrentStepSchema({
      //     schema: fallbackSchema.schema,
      //     uiSchema: fallbackSchema.uiSchema || {},
      //     title: fallbackSchema.title,
      //   });
      // }
    }
  };

  // Initialize first step only once
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      setIsInitializing(true);
      await loadStepSchema(0, {});
      setIsInitializing(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNextStep = async () => {
    // Merge current form data with accumulated data
    const newAccumulatedData = { ...accumulatedData, ...currentFormData };
    setAccumulatedData(newAccumulatedData);

    if (isLastStep) {
      // All steps completed - pass params and mappings (already captured from last step's load response)
      onComplete(newAccumulatedData, lastStepMappings);
    } else {
      // Move to next step and load its schema
      const nextStepIndex = currentStepIndex + 1;
      await loadStepSchema(nextStepIndex, newAccumulatedData);
      setCurrentStepIndex(nextStepIndex);
      setCurrentFormData({});
    }
  };

  const handlePreviousStep = () => {
    let prevIndex = currentStepIndex - 1;
    while (prevIndex >= 0 && autoSkippedSteps.has(stepKeys[prevIndex])) {
      prevIndex -= 1;
    }
    if (prevIndex >= 0) {
      setCurrentStepIndex(prevIndex);
      // Restore previous step's data from accumulated
      // Note: This is simplified - in production you might want to store each step's data separately
      setCurrentFormData({});
    }
  };

  const isFormValid = currentFormData && Object.keys(currentFormData).length > 0;
  const hasPreviousStep = stepKeys.slice(0, currentStepIndex).some((key) => !autoSkippedSteps.has(key));

  return (
    <div className='space-y-6'>
      {/* Step indicator */}
      <div className='flex items-center justify-between border-b border-secondary-border pb-4'>
        <div className='flex items-center gap-2'>
          {stepKeys.map((stepKey, index) => (
            <div key={stepKey} className='flex items-center'>
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                  index === currentStepIndex ? 'bg-primary-600 text-white' : index < currentStepIndex ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {index < currentStepIndex ? '✓' : index + 1}
              </div>
              {index < stepKeys.length - 1 && <div className='mx-2 h-0.5 w-8 bg-gray-200' />}
            </div>
          ))}
        </div>
        <span className='text-sm text-tertiary-600'>
          {t('COMMON:step')} {currentStepIndex + 1} {t('COMMON:of')} {totalSteps}
        </span>
      </div>

      {/* Step title */}
      {currentStepSchema?.title && <h3 className='text-lg font-medium text-primary-900'>{currentStepSchema.title}</h3>}

      {/* Form */}
      <Loadable isLoading={isInitializing || isLoadingStep}>
        {currentStepSchema && (
          <Form
            schema={currentStepSchema.schema}
            uiSchema={currentStepSchema.uiSchema}
            formData={currentFormData}
            onChange={({ formData }) => setCurrentFormData(formData)}
            validator={validator}
          >
            <Fragment />
          </Form>
        )}
      </Loadable>

      {/* Navigation buttons */}
      <div className='flex justify-between pt-4'>
        <button
          type='button'
          onClick={handlePreviousStep}
          disabled={!hasPreviousStep}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            !hasPreviousStep ? 'cursor-not-allowed text-gray-400' : 'text-primary-600 hover:text-primary-700'
          }`}
        >
          {t('COMMON:previous')}
        </button>
        <button
          type='button'
          onClick={handleNextStep}
          disabled={!isFormValid || isLoadingStep}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            !isFormValid || isLoadingStep ? 'cursor-not-allowed bg-gray-300 text-gray-500' : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {isLastStep ? t('COMMON:confirm') : t('COMMON:next')}
        </button>
      </div>
    </div>
  );
};
